import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Save,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { courseAPI } from '../../../services/api';

const CourseCreate = () => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  // Course form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    description: '',
    durationValue: '',
    durationUnit: 'months',
    numberOfSemesters: 1,
    eligibility: '',
    feesAmount: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await courseAPI.getCategories();
      setCategories(response.data.data?.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.category || !formData.durationValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        category: formData.category,
        description: formData.description,
        duration: {
          value: Number(formData.durationValue),
          unit: formData.durationUnit,
        },
        numberOfSemesters: Number(formData.numberOfSemesters),
        semesters: [],
        eligibility: formData.eligibility,
        fees: {
          amount: Number(formData.feesAmount) || 0,
          currency: 'INR',
        },
      };

      const response = await courseAPI.create(payload);
      toast.success('Course created successfully! You can now add semesters and subjects.');
      // Navigate to edit page to add semesters
      const courseId = response.data.data?.course?._id || response.data.data?._id;
      navigate(`/admin/courses/edit/${courseId}`);
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error(error.response?.data?.message || 'Failed to create course');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Create New Course
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/courses')}>
          Back to Courses
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Create the basic course information here. After saving, you'll be able to add semesters and subjects to the course.
      </Alert>

      {/* Course Details Form */}
      <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Course Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Course Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g., Diploma in Computer Applications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Course Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ style: { textTransform: 'uppercase' } }}
                placeholder="e.g., DCA"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{ minWidth: 250 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Duration"
                name="durationValue"
                type="number"
                value={formData.durationValue}
                onChange={handleChange}
                required
                fullWidth
                inputProps={{ min: 1 }}
                placeholder="12"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth required sx={{ minWidth: 250 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  name="durationUnit"
                  value={formData.durationUnit}
                  onChange={handleChange}
                  label="Unit"
                >
                  <MenuItem value="months">Months</MenuItem>
                  <MenuItem value="years">Years</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Number of Semesters"
                name="numberOfSemesters"
                type="number"
                value={formData.numberOfSemesters}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 1 }}
                helperText="You can add semesters after creating the course"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Course Fees (INR)"
                name="feesAmount"
                type="number"
                value={formData.feesAmount}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                placeholder="25000"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Eligibility Criteria"
                name="eligibility"
                value={formData.eligibility}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., 10+2 or equivalent"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Enter course description..."
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/admin/courses')} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" startIcon={<Save />} disabled={saving}>
                  {saving ? 'Creating...' : 'Create Course'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CourseCreate;
